import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { shopAxios } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { ValidationError } from '../admin/components/ValidationError';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';

function tx(t: (k: string) => string, key: string, fallback: string): string {
    const v = t(key);
    return !v || v === key ? fallback : v;
}

/** Shop-only forgot password. Admin accounts use a separate stack. */
const ForgotPassword = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError('');
        if (!email.trim()) {
            setError(tx(t, 'page.forgotPassword.emailRequired', 'Email is required.'));
            scrollToFirstErrorField();
            return;
        }
        setSubmitting(true);
        try {
            await shopAxios.post('/auth/forgot-password', { email: email.trim() });
            setDone(true);
        } catch (err: unknown) {
            // Rate limit or unexpected — still avoid leaking account existence when possible
            if (err && typeof err === 'object' && 'response' in err) {
                const status = (err as { response?: { status?: number } }).response?.status;
                if (status === 429) {
                    setError(tx(t, 'error.toomanyattempts', 'Too many attempts. Please wait and try again.'));
                } else {
                    // Backend still returns 200 normally; any other failure is infrastructure
                    setDone(true);
                }
            } else {
                setDone(true);
            }
            scrollToFirstErrorField();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex min-h-dvh items-center justify-center px-4 py-8 sm:py-12">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-md sm:p-8">
                <h1 className="mb-2 text-2xl font-bold">
                    {tx(t, 'page.forgotPassword.title', 'Forgot password')}
                </h1>
                <p className="mb-6 text-sm text-gray-600">
                    {tx(
                        t,
                        'page.forgotPassword.subtitle',
                        'Enter your shop account email. If it exists and is confirmed, we will send a reset link.'
                    )}
                </p>

                {done ? (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
                            {tx(
                                t,
                                'page.forgotPassword.sent',
                                'If an account exists for that email, a password reset link has been sent. Check your inbox (and spam).'
                            )}
                        </div>
                        <Link to="/login" className="inline-block font-medium text-[var(--accent-color)] underline">
                            {tx(t, 'page.forgotPassword.backToLogin', 'Back to login')}
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label htmlFor="email" className="mb-1 block text-sm font-medium">
                                {tx(t, 'form.login.email', 'Email')}
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full rounded border p-2"
                                required
                                disabled={submitting}
                                autoComplete="email"
                            />
                        </div>
                        <ValidationError error={error} />
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full rounded bg-[var(--accent-color)] px-4 py-2 font-medium text-white disabled:opacity-60"
                        >
                            {submitting
                                ? tx(t, 'state.loading', 'Sending…')
                                : tx(t, 'page.forgotPassword.submit', 'Send reset link')}
                        </button>
                        <p className="text-center text-sm text-gray-600">
                            <Link to="/login" className="underline">
                                {tx(t, 'page.forgotPassword.backToLogin', 'Back to login')}
                            </Link>
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ForgotPassword;
